import { useCallback, useEffect, useRef, useState } from 'react';
import {
  isErpTaskSubmissionResponse,
  isTaskPollingCancelledError,
  postErpAsyncTask,
  waitForErpTasks
} from '@/lib/task-api';
import { useTaskPollingSignal } from '@/hooks/use-task-polling-signal';
import { normalizeEndpoint } from '@/lib/normalize-endpoint';
import { extractApiErrorMessage } from '@/lib/error-utils';
import { useAuth } from '@/contexts/auth-context';
import { ApiResponse } from '@/types/api.type';
import { toast } from '@/hooks/use-toast';
import api from '@/lib/api';

interface ReportRequestOptions {
  requestExtras?: Record<string, any>;
}

interface UseDataConfig {
  endpoint: string;
  searchData?: any;
  pageSize?: number;
  prefetch?: boolean;
  wrapObjectResults?: boolean;
  preserveObjectResponse?: boolean;
  taskQueue?: boolean;
  taskQueueIntervalMs?: number;
}

interface UseDataReturn<TData, TInput> {
  data: TData[];
  isLoading: boolean;
  error: string | null;
  totalItems: number;
  currentPage: number;
  handlePageChange: (page: number) => Promise<void>;
  getReport: (data: TInput, pageOrOptions?: number | ReportRequestOptions, searchPageSize?: number) => Promise<void>;
  getAll: (data?: TInput, pageSize?: number) => Promise<TData[]>;
  refreshData: () => Promise<void>;
  totalRow: TData | null;
}

interface PendingReportRequest<TInput> {
  requestBody: TInput;
  page: number;
  pageSize: number;
}

interface CachedReportResult<TData> {
  results: TData[];
  count: number;
  totalRow: TData | null;
}

const buildRequestBody = <TInput>(searchData: TInput, options?: ReportRequestOptions): TInput => {
  if (!options?.requestExtras || !searchData || typeof searchData !== 'object' || Array.isArray(searchData)) {
    return searchData;
  }

  return {
    ...(searchData as Record<string, any>),
    ...options.requestExtras
  } as TInput;
};

const resolveReportRequest = <TInput>(
  searchData: TInput,
  defaultPageSize: number,
  pageOrOptions?: number | ReportRequestOptions,
  searchPageSize?: number
) => {
  if (typeof pageOrOptions === 'number') {
    return {
      page: pageOrOptions,
      pageSize: searchPageSize || defaultPageSize,
      requestBody: searchData
    };
  }

  return {
    page: 0,
    pageSize: searchPageSize || defaultPageSize,
    requestBody: buildRequestBody(searchData, pageOrOptions)
  };
};

const resolveWrappedResults = <TData>(
  response: ApiResponse<TData>,
  wrapObjectResults: boolean,
  preserveObjectResponse: boolean
): TData[] => {
  if (wrapObjectResults && preserveObjectResponse) {
    return response ? [response as unknown as TData] : [];
  }

  const rawResults = response.results;

  if (!wrapObjectResults) {
    return rawResults || (response as unknown as TData[]) || [];
  }

  if (Array.isArray(rawResults)) {
    return rawResults;
  }

  if (rawResults) {
    return [rawResults as TData];
  }

  return response ? [response as unknown as TData] : [];
};

export const useReport = <TData = any, TInput = any>(
  config: UseDataConfig,
  initialList?: TData[]
): UseDataReturn<TData, TInput> => {
  const [data, setData] = useState<TData[]>(initialList || []);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [lastSearchData, setLastSearchData] = useState<TInput | null>(null);
  const [pendingRequest, setPendingRequest] = useState<PendingReportRequest<TInput> | null>(null);
  const [totalRow, setTotalRow] = useState<TData | null>(null);

  const pageCacheRef = useRef<Map<string, CachedReportResult<TData>>>(new Map());
  const getTaskPollingSignal = useTaskPollingSignal();

  const { entity, setLoading: setGlobalLoading } = useAuth();
  const {
    endpoint,
    pageSize = 50,
    wrapObjectResults = false,
    preserveObjectResponse = false,
    taskQueue = false,
    taskQueueIntervalMs = 5000
  } = config;
  const normalizedEndpoint = normalizeEndpoint(endpoint);
  const prefetchEnabled = config.prefetch !== false;

  const resolveTaskResponseData = useCallback(
    async (responseData: any): Promise<ApiResponse<TData>> => {
      if (!taskQueue || !entity?.slug || !isErpTaskSubmissionResponse(responseData)) {
        return responseData;
      }

      const [taskDetail] = await waitForErpTasks<ApiResponse<TData>>({
        entitySlug: entity.slug,
        tasks: [responseData],
        intervalMs: taskQueueIntervalMs,
        signal: getTaskPollingSignal()
      });

      return (taskDetail.result ?? responseData) as ApiResponse<TData>;
    },
    [entity?.slug, getTaskPollingSignal, taskQueue, taskQueueIntervalMs]
  );

  const buildCacheKey = useCallback(
    (page: number, requestPageSize: number, requestBody?: TInput | null) =>
      JSON.stringify({
        entitySlug: entity?.slug,
        endpoint: normalizedEndpoint,
        page,
        pageSize: requestPageSize,
        searchData: requestBody ?? null,
        configSearchData: config.searchData ?? null,
        wrapObjectResults,
        preserveObjectResponse,
        taskQueue,
        taskQueueIntervalMs
      }),
    [
      config.searchData,
      entity?.slug,
      normalizedEndpoint,
      preserveObjectResponse,
      taskQueue,
      taskQueueIntervalMs,
      wrapObjectResults
    ]
  );

  const performFetch = useCallback(
    async (page: number, requestPageSize: number, requestBody?: TInput | null): Promise<CachedReportResult<TData>> => {
      const requestUrl = `/entities/${entity?.slug}/erp/${normalizedEndpoint}`;
      const requestConfig = {
        params: {
          ...config.searchData,
          page: page + 1,
          page_size: requestPageSize
        }
      };
      const response = taskQueue
        ? await postErpAsyncTask<ApiResponse<TData>, TInput | null>(requestUrl, requestBody ?? null, requestConfig)
        : await api.post<ApiResponse<TData>>(requestUrl, requestBody, requestConfig);
      const responseData = await resolveTaskResponseData(response.data);

      return {
        results: resolveWrappedResults(responseData, wrapObjectResults, preserveObjectResponse),
        count: responseData.count || 0,
        totalRow: (((responseData as any).total || (responseData as any).totals || null) as TData | null) ?? null
      };
    },
    [
      config.searchData,
      entity?.slug,
      normalizedEndpoint,
      preserveObjectResponse,
      resolveTaskResponseData,
      wrapObjectResults
    ]
  );

  const prefetchNextPage = useCallback(
    async (page: number, requestPageSize: number, requestBody?: TInput | null, total = 0) => {
      if (!entity?.slug) return;

      const totalPages = Math.ceil((total || 0) / requestPageSize);
      const nextPage = page + 1;
      if (totalPages <= 1 || nextPage >= totalPages) {
        return;
      }

      const nextCacheKey = buildCacheKey(nextPage, requestPageSize, requestBody);
      if (pageCacheRef.current.has(nextCacheKey)) {
        return;
      }

      try {
        const nextResult = await performFetch(nextPage, requestPageSize, requestBody);
        pageCacheRef.current.set(nextCacheKey, nextResult);
      } catch {
        return;
      }
    },
    [buildCacheKey, entity?.slug, performFetch]
  );

  const fetchData = useCallback(
    async (paginationParams?: { page?: number; pageSize?: number }, searchData?: TInput | null, force = false) => {
      if (!entity?.slug) return;

      const requestPage = paginationParams?.page ?? 0;
      const requestPageSize = paginationParams?.pageSize || pageSize;
      const requestBody = searchData !== undefined ? searchData : lastSearchData;
      const cacheKey = buildCacheKey(requestPage, requestPageSize, requestBody);
      const cachedResult = !force ? pageCacheRef.current.get(cacheKey) : undefined;

      if (cachedResult) {
        setData(cachedResult.results);
        setTotalItems(cachedResult.count);
        setTotalRow(cachedResult.totalRow);
        setError(null);
        if (prefetchEnabled) {
          void prefetchNextPage(requestPage, requestPageSize, requestBody, cachedResult.count);
        }
        return;
      }

      setIsLoading(true);
      setGlobalLoading(true);
      try {
        const result = await performFetch(requestPage, requestPageSize, requestBody);
        pageCacheRef.current.set(cacheKey, result);

        setData(result.results);
        setTotalItems(result.count);
        setTotalRow(result.totalRow);
        setError(null);

        if (prefetchEnabled) {
          void prefetchNextPage(requestPage, requestPageSize, requestBody, result.count);
        }
      } catch (fetchError: any) {
        if (isTaskPollingCancelledError(fetchError)) return;
        const errorMsg = extractApiErrorMessage({ data: fetchError });
        setError(errorMsg);
        setData([]);
        setTotalItems(0);
        setTotalRow(null);
        toast.error(errorMsg);
        throw fetchError;
      } finally {
        setIsLoading(false);
        setGlobalLoading(false);
      }
    },
    [
      buildCacheKey,
      entity?.slug,
      lastSearchData,
      pageSize,
      performFetch,
      prefetchEnabled,
      prefetchNextPage,
      setGlobalLoading
    ]
  );

  const getReport = useCallback(
    async (itemData: TInput, pageOrOptions?: number | ReportRequestOptions, searchPageSize?: number): Promise<void> => {
      const request = resolveReportRequest(itemData, pageSize, pageOrOptions, searchPageSize);

      setLastSearchData(itemData);
      setCurrentPage(request.page);
      pageCacheRef.current.clear();

      if (!entity?.slug) {
        setPendingRequest(request);
        return;
      }

      setPendingRequest(null);
      await fetchData({ page: request.page, pageSize: request.pageSize }, request.requestBody, true);
    },
    [entity?.slug, fetchData, pageSize]
  );

  const getAll = useCallback(
    async (searchData?: TInput, customPageSize = 200): Promise<TData[]> => {
      if (!entity?.slug) return [];

      setIsLoading(true);
      setGlobalLoading(true);
      try {
        let page = 1;
        let all: TData[] = [];
        let totalCount = 0;
        const body = searchData || lastSearchData || config.searchData;

        while (true) {
          const response = await api.post<ApiResponse<TData>>(
            `/entities/${entity.slug}/erp/${normalizedEndpoint}`,
            body,
            {
              params: {
                page,
                page_size: customPageSize
              }
            }
          );
          const responseData = await resolveTaskResponseData(response.data);

          const pageResults = (responseData as any).results || [];
          totalCount = (responseData as any).count ?? totalCount;
          all = all.concat(pageResults);

          if (pageResults.length < customPageSize || (totalCount && all.length >= totalCount)) {
            break;
          }

          page += 1;
        }

        setError(null);
        return all;
      } catch (fetchError: any) {
        if (isTaskPollingCancelledError(fetchError)) return [];
        const errorMsg = extractApiErrorMessage({ data: fetchError });
        setError(errorMsg);
        toast.error(errorMsg);
        throw fetchError;
      } finally {
        setIsLoading(false);
        setGlobalLoading(false);
      }
    },
    [config.searchData, entity?.slug, lastSearchData, normalizedEndpoint, resolveTaskResponseData, setGlobalLoading]
  );

  useEffect(() => {
    if (!entity?.slug || !pendingRequest) {
      return;
    }

    fetchData(
      { page: pendingRequest.page, pageSize: pendingRequest.pageSize },
      pendingRequest.requestBody,
      true
    ).finally(() => {
      setPendingRequest(null);
    });
  }, [entity?.slug, pendingRequest, fetchData]);

  const handlePageChange = useCallback(
    async (page: number) => {
      setCurrentPage(page);
      await fetchData({ page, pageSize });
    },
    [fetchData, pageSize]
  );

  const refreshData = useCallback(async (): Promise<void> => {
    pageCacheRef.current.clear();
    await fetchData({ page: currentPage, pageSize }, undefined, true);
  }, [currentPage, fetchData, pageSize]);

  useEffect(() => {
    pageCacheRef.current.clear();
  }, [
    entity?.slug,
    normalizedEndpoint,
    pageSize,
    preserveObjectResponse,
    taskQueue,
    taskQueueIntervalMs,
    wrapObjectResults,
    config.searchData
  ]);

  return {
    data,
    isLoading,
    error,
    totalItems,
    currentPage,
    handlePageChange,
    getReport,
    getAll,
    refreshData,
    totalRow
  };
};
